import { some } from "lodash";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { enums, http } from "../../common/lib";
import config from "../../config";
import actions from "../actions";
import { logToSentry } from "../../config/Sentry";
import { actionCreators, actionTypes } from "./actions";

const anyPendingAltAssets = (data: Array<any>) =>
    some(data, (item) => item.isResolved === false);

const anyPendingAuditAssets = (data: Array<any>) =>
    some(
        data.filter(
            (item) =>
                item.isAssigned === true &&
                item.isExpected === true &&
                item.isMandatory === true
        ),
        (item) => item.isApproved === null
    );

const isReissueNeeded = (data: Array<any>) =>
    some(
        data,
        (item) => item.reissuedStatus === enums.status.reissued.reissued
    );

const isReviewComplete = (auditAssets: Array<any>, altAssets: Array<any>) =>
    !(anyPendingAltAssets(altAssets) || anyPendingAuditAssets(auditAssets));

const makeFilters = () => ({
    expectedNonMandatory: (item: any) => 
        item.isMandatory === false,
    unexpected: (item: any) =>
        item.isResolved === true || item.isResolved === false,
    unexpectedPending: (item: any) => item.isResolved === false,
    unexpectedResolved: (item: any) => item.isResolved === true,
    expected: (item: any) => 
        item.isExpected === true && item.isMandatory === true,

    expectedApproved: (item: any) =>
        (item.isMandatory === true &&
            item.isApproved === true && item.isExpected === true) ||
        (item.isMandatory === false && item.isApproved === true),

    expectedUnchecked: (item: any) =>
        !item.sharedValidationAsset &&
        item.isAssigned === false &&
        item.reissuedStatus === null &&
        item.isExpected === true &&
        item.isMandatory === true,
    expectedUncheckedPending: (item: any) =>
        !item.sharedValidationAsset &&
        item.isAssigned === false &&
        item.reissuedStatus === null &&
        item.isExpected === true &&
        item.isMandatory === true,
    expectedAutoverified: (item: any) =>
        !item.sharedValidationAsset && 
        item.isPreApproved == true &&
        item.isExpected === true &&
        item.isMandatory === true,
    expectedAutoverifiedPending: (item: any) =>
        !item.sharedValidationAsset &&
        item.isPreApproved == true &&
        item.isApproved === null &&
        item.isExpected === true &&
        item.isMandatory === true,
    expectedSeen: (item: any) =>
        item.isExpected === true &&
        item.isAssigned === true &&
        item.status === enums.status.assetStatus.seen &&
        item.isMandatory === true,
    expectedSeenPending: (item: any) =>
        item.isExpected === true &&
        item.isAssigned === true &&
        item.isApproved === null &&
        item.status === enums.status.assetStatus.seen &&
        item.isMandatory === true,
    expectedPending: (item: any) =>
        item.isApproved === null && item.isExpected === true && item.isMandatory === true,

    expectedReissued: (item: any) =>
        !item.sharedValidationAsset &&
        item.isExpected === true &&
        item.reissuedStatus === enums.status.reissued.reissued &&
        item.isMandatory === true,
    expectedReissuedPending: (item: any) =>
        !item.sharedValidationAsset &&
        item.isExpected === true &&
        item.isAssigned === true &&
        item.reissuedStatus === enums.status.reissued.reissued &&
        item.isMandatory === true,
    expectedRejected: (item: any) =>
        (item.isExpected === true && 
            item.isMandatory === true &&
            item.isApproved === false) ||
        (item.isMandatory === false && item.isApproved === false),    

    expectedExcluded: (item: any) =>
        !item.sharedValidationAsset &&
        item.status === enums.status.assetStatus.excluded &&
        item.isExpected === true &&
        item.isMandatory === true,

    expectedExcludedPending: (item: any) =>
        !item.sharedValidationAsset &&
        item.status === enums.status.assetStatus.excluded &&
        item.isExpected === true &&
        item.isApproved === null &&
        item.isMandatory === true,
    expectedValidatedVisibilityApproved: (item: any) =>
        item.sharedValidationAsset !== null && item.isApproved === true,
    expectedValidatedVisibilityPending: (item: any) =>
        item.sharedValidationAsset !== null &&
        item.isApproved === null &&
        item.isAssigned === true,
    expectedValidatedVisibilityReissued: (item: any) =>
        item.sharedValidationAsset !== null &&
        !item.sharedValidationAsset &&
        item.reissuedStatus === enums.status.reissued.reissued,
    auditAssetsRejected: (item: any) =>
        !item.sharedValidationAsset && item.isApproved === false,
    expectedValidatedVisibility: (item: any) =>
        item.sharedValidationAsset !== null,
    expectedValidatedVisibilityPending: (item: any) =>
        item.sharedValidationAsset !== null &&
        item.isApproved === null &&
        item.isAssigned === true,
    expectedValidatedVisibilityReissued: (item: any) =>
        item.sharedValidationAsset !== null &&
        item.reissuedStatus === enums.status.reissued.reissued,
    expectedValidatedVisibilityRejected: (item: any) =>
        item.sharedValidationAsset !== null && item.isApproved === false
});

function* filter(action) {
    try {
        const { altAssets, auditAssets, auditReview } = yield select(
            (state) => state
        );
        let status = "";
        if (action.payload.status) {
            status = action.payload.status;
        } else {
            status = auditReview.status;
        }
        const statusArray = Array.isArray(status) ? status : [status];

        const sharedValidatedAssets = auditAssets.data.filter(
            (item) => item.sharedValidationAsset
        );

        const applyFilters = (filterNames: string[]) => {
            // Create filters object
            const filters = makeFilters();

            // Combine the filters based on the array of filter names
            return (item: any) => {
                // Loop through each filter in the array and apply it to the item

                return filterNames.some((filterName) => {
                    const filter = filters[filterName];
                    const result = filter ? filter(item) : false; // Ensure the filter exists
                    return result;
                });
            };
        };
        const filter = applyFilters(statusArray);

        //Function to apply the filters

        if (altAssets.data.length) {
            const ids = altAssets.data.reduce((aggregate, value) => {
                filter && filter(value) && aggregate.push(value.id);
                return aggregate;
            }, []);
            yield put(actionCreators.altAssetsFilterSet({ ids }, status));
        }
        if (auditAssets.data.length) {
            const ids = auditAssets.data.reduce((aggregate, value) => {
                filter && filter(value) && aggregate.push(value.id);
                return aggregate;
            }, []);
            yield put(actionCreators.auditAssetsFilterSet({ ids }, status));
        }
        if (sharedValidatedAssets.length) {
            const ids = sharedValidatedAssets.reduce((aggregate, value) => {
                filter && filter(value) && aggregate.push(value.id);
                return aggregate;
            }, []);
            yield put(
                actionCreators.validatedVisibilityFilterSet({ ids }, status)
            );
        }
        if (sharedValidatedAssets.length) {
            const ids = sharedValidatedAssets.reduce((aggregate, value) => {
                filter && filter(value) && aggregate.push(value.id);
                return aggregate;
            }, []);
            yield put(
                actionCreators.validatedVisibilityFilterSet({ ids }, status)
            );
        }
    } catch (error) {
        console.error(error);
        logToSentry("On audit review filter:", { error });
    }
}

function* get(action) {
    const { auditId } = action.payload;
    const url = `${config.api.paths.auditReview}?audit=${auditId}`;
    try {
        yield all([
            put(actions.assetAuditors.create.clear()),
            put(actions.auditControllers.create.clear()),
            put(actions.assets.create.clear()),
            put(actions.auditAssets.create.clear()),
            put(actions.auditAssignments.create.clear()),
            put(actions.auditLocations.create.clear()),
            put(actions.dealerContacts.create.clear())
        ]);
        const { data } = yield call(http.authorized.get, url);
        const {
            assetAuditors,
            assets,
            assetControllers,
            auditAssets,
            auditAssignments,
            auditLocations,
            auditors,
            locations,
            audit,
            dealerContacts
        } = data;
        const auditControllers = assetControllers;
        /**  Merging data of asset Controller to assetAudior
    Because all of the places assetAudior is used.
    As per the requirement the controller is used in one place inside auditAsset
    asset Controller data can be identified by the key
  */

        yield all([
            put(actions.accounts.create.merge(auditors)),
            put(actions.assetAuditors.create.set(assetAuditors)),
            put(actions.auditControllers.create.set(auditControllers)),
            put(actions.assets.create.set(assets)),
            put(actions.auditAssets.create.set(auditAssets)),
            put(actions.auditAssignments.create.set(auditAssignments)),
            put(actions.auditLocations.create.set(auditLocations)),
            put(actions.locations.create.merge(locations)),
            put(actions.audits.create.itemSet(audit)),
            put(actions.auditsList.create.itemSet(audit)),
            put(actions.dealerContacts.create.set(dealerContacts)),
            put(actionCreators.getSuccess(data))
        ]);
    } catch (error) {
        console.error(error);
        logToSentry("On audit review get:", { error });
        yield put(actionCreators.getFailure(error));
    }
}

function* summary() {
    try {
        const { altAssets, auditAssets, auditControllers } = yield select(
            (state) => state
        );
        const filters = makeFilters();

        if (auditAssets.data.length) {
            const { data } = auditAssets;
            const nonMandatoryAssets = data.filter(
                (item) => item.isMandatory === false
            );  
            const assets = data.filter(
                (item) => !item.sharedValidationAsset && 
                item.isExpected === true &&
                item.isMandatory === true
            );

            const sharedValidatedAssets = data.filter(
                (item) => item.sharedValidationAsset
            );
            const summary = {
                approved: assets.filter(filters.expectedApproved).length,
                unchecked: assets.filter(filters.expectedUnchecked).length,
                excluded: assets.filter(filters.expectedExcluded).length,
                autoVerified: assets.filter(filters.expectedAutoverified)
                    .length,
                autoVerifiedPending: assets.filter(
                    filters.expectedAutoverifiedPending
                ).length,
                seen: assets.filter(filters.expectedSeen).length,
                seenPending: assets.filter(filters.expectedSeenPending).length,
                expectedAutoverifiedPending: assets.filter(
                    filters.expectedAutoverifiedPending
                ).length,
                expectedUncheckedPending: assets.filter(
                    filters.expectedUnchecked
                ).length,
                excludedPending: assets.filter(filters.expectedExcludedPending)
                    .length,
                reissuedPending: assets.filter(filters.expectedReissuedPending)
                    .length,
                reissued: assets.filter(filters.expectedReissued).length,
                rejected: assets.filter(filters.expectedRejected).length,
                total: assets.length,
                nonMandatoryTotal: nonMandatoryAssets.length
            };
            yield put(actionCreators.auditAssetsSummarySet(summary));

            if (sharedValidatedAssets.length > 0) {
                const validatedVisibilitySummary = {
                    approved: sharedValidatedAssets.filter(
                        filters.expectedValidatedVisibilityApproved
                    ).length,
                    pending: sharedValidatedAssets.filter(
                        filters.expectedValidatedVisibilityPending
                    ).length,
                    reissued: sharedValidatedAssets.filter(
                        filters.expectedValidatedVisibilityReissued
                    ).length,
                    rejected: sharedValidatedAssets.filter(
                        filters.expectedValidatedVisibilityRejected
                    ).length,
                    total: sharedValidatedAssets.filter(
                        filters.expectedValidatedVisibility
                    ).length
                    //sharedValidatedAssets.length
                };
                yield put(
                    actionCreators.validatedVisibilitySummarySet(
                        validatedVisibilitySummary
                    )
                );
            }
        }
        if (altAssets.data.length) {
            const { data } = altAssets;
            const summary = {
                pending: data.filter(filters.unexpectedPending).length,
                resolved: data.filter(filters.unexpectedResolved).length,
                total: data.length
            };
            yield put(actionCreators.altAssetsSummarySet(summary));
        }
        yield put(
            actionCreators.completionSet(
                isReviewComplete(auditAssets.data, altAssets.data),
                isReissueNeeded(auditAssets.data)
            )
        );
    } catch (error) {
        console.error(error);
        logToSentry("On audit review summary:", { error });
    }
}

export default function* () {
    yield all([
        takeLatest(actions.altAssets.type.ALT_ASSETS_GET_SUCCESS, summary),
        takeLatest(
            actions.altAssets.type.ALT_ASSETS_BULK_UPDATE_POST_SUCCESS,
            filter
        ),
        takeLatest(
            actions.altAssets.type.ALT_ASSETS_BULK_UPDATE_POST_SUCCESS,
            summary
        ),
        takeLatest(actions.auditAssets.type.AUDIT_ASSETS_PATCH_SUCCESS, filter),
        takeLatest(
            actions.auditAssets.type.AUDIT_ASSETS_PATCH_SUCCESS,
            summary
        ),
        takeLatest(actions.auditAssets.type.AUDIT_ASSETS_SET, filter),
        takeLatest(actions.auditAssets.type.AUDIT_ASSETS_SET, summary),
        takeLatest(actions.auditAssets.type.AUDIT_ASSET_UPDATE_ASSET, summary),
        takeLatest(actionTypes.AUDIT_REVIEW_GET_REQUEST, get),
        takeLatest(actionTypes.AUDIT_REVIEW_STATUS_SET, filter)
    ]);
}
